import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, Menu, X, Globe, Home, ClipboardList, MapPin, User, Wallet, Instagram, Facebook, Mail, LayoutDashboard } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth, useSettings } from "@/context/AppContext";
import { cn, isStaff } from "@/lib/utils";

export const LangSwitch = ({ light }) => {
  const { i18n } = useTranslation();
  const next = i18n.language === "ar" ? "en" : "ar";
  return (
    <button data-testid="lang-switch" onClick={() => i18n.changeLanguage(next)} className={cn("inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors", light ? "border-white/30 text-white hover:bg-white/10" : "border-border hover:bg-muted")}>
      <Globe size={14} /> {next === "ar" ? "العربية" : "English"}
    </button>
  );
};

export const Header = () => {
  const { t } = useTranslation();
  const { user, logout, unread } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const dash = isStaff(user) ? "/admin" : user?.role === "partner" ? "/partner" : "/dashboard";
  const links = [["/services", t("nav.services")], ["/search", t("nav.search")], ["/nearby", t("nav.nearby")], ["/how-it-works", t("nav.how")]];
  return (
    <header className="sticky top-0 z-40 border-b border-white/40 bg-white/75 backdrop-blur-xl">
      <div className="z-container flex h-14 items-center justify-between gap-2 sm:gap-4 md:h-16">
        <Logo compact size={46} />
        <nav className="hidden items-center gap-1 lg:flex">
          {links.map(([to, l]) => <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`} className={({ isActive }) => cn("rounded-full px-4 py-2 text-sm font-semibold transition-colors hover:bg-muted", isActive && "bg-muted text-[#1E3A8A]")}>{l}</NavLink>)}
        </nav>
        <div className="flex items-center gap-2">
          <LangSwitch />
          {user ? (
            <>
              <Link to="/notifications" data-testid="header-notifications" className="relative rounded-full p-2 hover:bg-muted"><Bell size={20} />{unread > 0 && <span data-testid="unread-badge" className="absolute -top-0.5 -end-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-[#10B981] px-1 text-[10px] font-bold text-white">{unread}</span>}</Link>
              <Link to={dash} data-testid="header-dashboard" className="hidden rounded-full bg-[#0B192C] px-4 py-2 text-sm font-bold text-white hover:bg-[#1E3A8A] lg:inline-flex">{t("nav.dashboard")}</Link>
              <button data-testid="header-logout" onClick={() => { logout(); nav("/"); }} className="hidden text-sm font-semibold text-muted-foreground hover:text-foreground lg:inline">{t("nav.logout")}</button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login" className="hidden rounded-full px-4 py-2 text-sm font-bold hover:bg-muted lg:inline-flex">{t("nav.login")}</Link>
              <Link to="/request" data-testid="header-request" className="z-btn-primary hidden whitespace-nowrap !px-4 !py-2 text-sm lg:inline-flex">{t("nav.request_now")}</Link>
            </>
          )}
          <button data-testid="mobile-menu-toggle" className="rounded-full p-2 hover:bg-muted lg:hidden" onClick={() => setOpen(!open)}>{open ? <X size={22} /> : <Menu size={22} />}</button>
        </div>
      </div>
      {open && (
        <div className="border-t bg-white px-4 py-3 lg:hidden animate-in">
          {[...links, ["/become-partner", t("nav.become_partner")], user ? [dash, t("nav.dashboard")] : ["/login", t("nav.login")], user ? null : ["/register", t("nav.register")]].filter(Boolean).map(([to, l]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)} className="block rounded-xl px-3 py-2.5 font-semibold hover:bg-muted">{l}</Link>
          ))}
          {user && <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="block w-full rounded-xl px-3 py-2.5 text-start font-semibold text-red-600 hover:bg-muted">{t("nav.logout")}</button>}
        </div>
      )}
    </header>
  );
};

export const BottomNav = () => {
  const { t } = useTranslation();
  const { user, unread } = useAuth();
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  const items = user?.role === "partner"
    ? [["/partner", Home, t("nav.home")], ["/orders", ClipboardList, t("nav.requests")], ["/wallet", Wallet, t("nav.wallet")], ["/notifications", Bell, t("nav.notifications")], ["/account", User, t("nav.account")]]
    : [["/", Home, t("nav.home")], ["/orders", ClipboardList, t("nav.orders")], ["/nearby", MapPin, t("nav.nearby")], ["/notifications", Bell, t("nav.notifications")], [user ? "/account" : "/login", User, t("nav.account")]];
  return (
    <nav data-testid="bottom-nav" className="fixed inset-x-0 bottom-0 z-40 border-t border-white/50 bg-white/90 backdrop-blur-xl md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="grid grid-cols-5">
        {items.map(([to, Icon, label]) => (
          <NavLink key={to + label} to={to} end={to === "/"} data-testid={`bottom-nav-${label}`} className={({ isActive }) => cn("relative flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-semibold text-muted-foreground transition-colors", isActive && "text-[#06B6D4]")}>
            <Icon size={21} />{label}
            {Icon === Bell && unread > 0 && <span className="absolute top-1.5 end-1/4 h-2 w-2 rounded-full bg-[#10B981]" />}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

export const Footer = () => {
  const { t } = useTranslation();
  const s = useSettings();
  const { user } = useAuth();
  return (
    <footer className="mt-20 bg-[#0B192C] text-white z-noise">
      <div className="z-container grid gap-10 py-14 md:grid-cols-4">
        <div className="md:col-span-1"><Logo light size={44} /><p className="mt-4 text-sm text-white/70">{t("footer.about")}</p>
          <div className="mt-5 flex gap-3">
            <a data-testid="social-instagram" href={s.instagram_url} target="_blank" rel="noreferrer" className="rounded-full bg-white/10 p-2.5 transition-colors hover:bg-[#06B6D4]"><Instagram size={18} /></a>
            <a data-testid="social-facebook" href={s.facebook_url} target="_blank" rel="noreferrer" className="rounded-full bg-white/10 p-2.5 transition-colors hover:bg-[#1E3A8A]"><Facebook size={18} /></a>
            {s.support_email && <a data-testid="social-email" href={`mailto:${s.support_email}`} className="rounded-full bg-white/10 p-2.5 transition-colors hover:bg-[#10B981]"><Mail size={18} /></a>}
          </div>
        </div>
        <div><h4 className="mb-4 font-bold text-[#06B6D4]">{t("footer.customers")}</h4>{[["/request", t("nav.request_now")], ["/services", t("nav.services")], ["/search", t("nav.search")], ["/nearby", t("nav.nearby")], ["/how-it-works", t("nav.how")], ["/faq", t("nav.faq")]].map(([to, l]) => <Link key={to} to={to} className="block py-1 text-sm text-white/75 hover:text-white">{l}</Link>)}</div>
        {user?.role === "partner" ? <div data-testid="footer-partners"><h4 className="mb-4 font-bold text-[#10B981]">{t("footer.partners")}</h4>{[["/partner", t("partner.dashboard")], ["/legal/partner-terms", t("footer.partner_terms")], ["/legal/wallet-terms", t("footer.wallet_terms")], ["/legal/lead-fee-policy", t("footer.lead_fee_policy")], ["/about", t("nav.about")], ["/contact", t("nav.contact")]].map(([to, l]) => <Link key={to} to={to} className="block py-1 text-sm text-white/75 hover:text-white">{l}</Link>)}</div> : <div data-testid="footer-company"><h4 className="mb-4 font-bold">{t("footer.company", "زيفا")}</h4>{[["/about", t("nav.about")], ["/contact", t("nav.contact")], ["/how-it-works", t("nav.how")]].map(([to, l]) => <Link key={to} to={to} className="block py-1 text-sm text-white/75 hover:text-white">{l}</Link>)}</div>}
        <div data-testid="footer-legal"><h4 className="mb-4 font-bold">{t("footer.legal")}</h4>{[["privacy-policy", t("footer.p_privacy")], ["terms", t("footer.p_terms")], ["customer-terms", t("footer.p_customer")], ["consent-policy", t("footer.p_consent")], ["cancellation-policy", t("footer.p_cancel")], ["complaint-policy", t("footer.p_complaint")]].map(([slug, l]) => <Link key={slug} to={`/legal/${slug}`} className="block py-1 text-sm text-white/75 hover:text-white">{l}</Link>)}</div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/50">© {new Date().getFullYear()} ZEEVA PRO | زيفا برو — zeeva-pro.com — {t("footer.rights")}</div>
    </footer>
  );
};

export const Layout = ({ children, noFooter }) => (
  <div className="flex min-h-screen flex-col">
    <Header />
    <main className="flex-1 pb-safe">{children}</main>
    {!noFooter && <div className="hidden md:block"><Footer /></div>}
    <div className="md:hidden"><Footer /></div>
    <BottomNav />
  </div>
);

export const Page = ({ title, subtitle, children, actions, testId }) => (
  <div className="z-container py-8 md:py-12" data-testid={testId}>
    {(title || actions) && (
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>{title && <h1 className="text-2xl font-extrabold text-[#0B192C] md:text-3xl">{title}</h1>}{subtitle && <p className="mt-1 text-sm text-muted-foreground md:text-base">{subtitle}</p>}</div>
        {actions}
      </div>
    )}
    {children}
  </div>
);

export const Empty = ({ text, icon: Icon = LayoutDashboard }) => (
  <div className="z-card grid place-items-center gap-3 p-12 text-center text-muted-foreground"><Icon size={40} className="text-[#06B6D4]/60" /><p>{text}</p></div>
);
